import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { fetchAudienceInterests, fetchSegments, type AudienceInterest } from "../lib/api";
import { DEMO_MODE } from "../lib/demo-data";
import { DataTable } from "../components/DataTable";
import { EmptyState } from "../components/primitives";
import { roleCan, useRole } from "../lib/roles";
import { useSegmentStore } from "../lib/segment-store";
import { PageSkeleton } from "../components/PageSkeleton";
import { SegmentWorkspace } from "../components/SegmentWorkspace";
import { AccessDenied } from "./NewsletterEditorPage";
import { PageHeader, PrimaryButton } from "../components/primitives";

export function SegmentsPage() {
  // Saved segments with estimated sizes exist only as sample data; the live
  // site shows who actually follows what.
  return DEMO_MODE ? <DemoSegmentsPage /> : <TopicsAudience />;
}

/**
 * Who receives the newsletter: every subscriber follows one or more topics,
 * and a newsletter goes to the followers of its topics. Counted from
 * newsletter.interests, the same table a send's audience is drawn from.
 */
function TopicsAudience() {
  const { role } = useRole();
  const q = useQuery({ queryKey: ["audience-interests"], queryFn: () => fetchAudienceInterests() });
  const rows = q.data?.interests ?? [];
  const biggest = rows.reduce((m, r) => Math.max(m, r.subscribers), 0);

  const columns = useMemo<ColumnDef<AudienceInterest>[]>(() => [
    { id: "topic", header: "Topic", accessorFn: (r) => r.name_ar || r.name_en || r.slug || "",
      cell: (c) => (
        <span className="au-topic">
          {c.row.original.name_ar && <span lang="ar" dir="rtl">{c.row.original.name_ar}</span>}
          {c.row.original.name_en && <span className="au-topic-en">{c.row.original.name_en}</span>}
        </span>
      ) },
    { accessorKey: "subscribers", header: "Following",
      cell: (c) => <span className="tabnum">{(c.getValue() as number).toLocaleString("en-US")}</span> },
    { id: "share", header: "Relative size", enableSorting: false,
      cell: (c) => (
        <span className="an-bar au-share" aria-hidden>
          <span style={{ width: `${biggest ? (c.row.original.subscribers / biggest) * 100 : 0}%` }} />
        </span>
      ) },
  ], [biggest]);

  if (role === "freelancer") return <AccessDenied need="Admins and editors" />;

  return (
    <div style={{ display: "grid", gap: "var(--ui-space-4)" }}>
      <PageHeader
        title="Audience"
        meta="Every subscriber follows one or more topics. A newsletter goes to the followers of the topics you pick."
        actions={<Link className="nl-btn-primary" to="/broadcasts">Send to a topic</Link>}
      />
      {q.isLoading ? (
        <PageSkeleton />
      ) : q.isError ? (
        <EmptyState title="Topics could not be loaded" body={(q.error as Error).message} hint="Reload the page to try again." />
      ) : rows.length === 0 ? (
        <EmptyState title="No topics yet" body="Topics appear here as soon as subscribers choose them on the signup form." hint="Counts come straight from subscriber sign-ups." />
      ) : (
        <>
          <DataTable data={rows} columns={columns} ariaLabel="Topics" searchPlaceholder="Search topics…"
            initialSort={[{ id: "subscribers", desc: true }]} getRowId={(r) => String(r.id)} paginate={rows.length > 25} />
          <p className="bc-hint">
            Someone following several topics is counted under each.
            {q.data?.approximate && " Counted from the most recent 20,000 topic choices."}
          </p>
        </>
      )}
    </div>
  );
}

function DemoSegmentsPage() {
  const { role } = useRole();
  const seed = useQuery({ queryKey: ["segments"], queryFn: fetchSegments });
  const store = useSegmentStore(seed.data);

  if (role === "freelancer") {
    return <AccessDenied need="Admins and editors" />;
  }

  if (!store.ready && seed.isLoading) return <PageSkeleton />;

  const data = store.segments ?? [];
  const totalAudience = data.reduce((s, x) => s + x.member_count, 0);

  return (
    <div style={{ display: "grid", gap: "var(--ui-space-4)" }}>
      <PageHeader
        title="Audience"
        meta="Step 1 — Audience. Combine country, category, language, and consent with AND/OR. Targeting is a segment, not per-person personalization."
        actions={
          <PrimaryButton onClick={() => document.getElementById("nl-segment-builder")?.scrollIntoView({ behavior: "smooth" })}>
            New segment
          </PrimaryButton>
        }
      />
      <div>
        <SegmentWorkspace
          segments={data}
          totalAudience={totalAudience}
          canWrite={roleCan(role, "segments.write")}
          onCreate={(input) => store.create(input)}
        />
      </div>
    </div>
  );
}
